<template>
  <v-dialog
    :model-value="modelValue"
    max-width="520"
    persistent
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="premium-adjust-modal">
      <header class="premium-adjust-modal__head">
        <h3 class="premium-adjust-modal__title">연장야간휴일 설정</h3>
        <button type="button" class="premium-adjust-modal__close" aria-label="닫기" @click="emit('cancel')">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </header>

      <div class="premium-adjust-modal__body">
        <div class="premium-adjust-modal__table-wrap">
          <table class="premium-adjust-modal__table">
            <thead>
              <tr>
                <th>구분</th>
                <th class="right">시간</th>
                <th class="right">지급가능액</th>
                <th class="right">지급할금액</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in displayRows" :key="row.key">
                <td class="premium-adjust-modal__kind">{{ row.label }}</td>
                <td class="right">{{ row.hoursLabel }}</td>
                <td class="right premium-adjust-modal__auto">{{ formatMoney(row.autoAmount) }}</td>
                <td class="right">
                  <input
                    type="text"
                    class="premium-adjust-modal__input"
                    inputmode="numeric"
                    :value="localDisplay[row.key]"
                    @input="onMoneyInput(row.key, $event)"
                    @blur="onMoneyBlur(row.key)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="returnPreview > 0" class="premium-adjust-modal__return">
          <span class="premium-adjust-modal__return-label">
            {{ isFamilyCare ? '기본급' : '연차수당' }} 환원 예상
          </span>
          <span class="premium-adjust-modal__return-value">{{ formatMoney(returnPreview) }}원</span>
        </div>

        <div class="premium-adjust-modal__holiday-action">
          <v-btn variant="flat" class="btn-edit" size="small" @click="emit('show-holiday-detail')">
            휴일수당 근무 내역
          </v-btn>
        </div>
      </div>

      <footer class="premium-adjust-modal__footer">
        <v-btn variant="text" class="btn-cancel" size="small" @click="emit('cancel')">취소</v-btn>
        <v-btn variant="flat" class="btn-confirm" size="small" @click="onApply">적용</v-btn>
      </footer>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import { formatWageNumber, parseWageNumber } from '@/composables/wage/useWageCalculator';
import { formatHomeWagePremiumHoursDisplay } from '@/policies';

export type PremiumAdjustKey = 'overtime' | 'night' | 'holiday';

export interface PremiumAdjustRowDef {
  key: PremiumAdjustKey;
  label: string;
  hours: number;
  autoAmount: number;
}

const props = defineProps<{
  modelValue: boolean;
  isFamilyCare: boolean;
  autoAllocatedTotal: number;
  rows: PremiumAdjustRowDef[];
  initialManual: Record<PremiumAdjustKey, number>;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  apply: [manual: Record<PremiumAdjustKey, number>];
  cancel: [];
  'show-holiday-detail': [];
}>();

const localAmounts = reactive<Record<PremiumAdjustKey, number>>({
  overtime: 0,
  night: 0,
  holiday: 0,
});

const localDisplay = reactive<Record<PremiumAdjustKey, string>>({
  overtime: '',
  night: '',
  holiday: '',
});

const syncFromProps = () => {
  for (const key of ['overtime', 'night', 'holiday'] as PremiumAdjustKey[]) {
    const val = Math.max(0, Number(props.initialManual[key]) || 0);
    localAmounts[key] = val;
    localDisplay[key] = val > 0 ? formatWageNumber(val) : '';
  }
};

watch(
  () => props.modelValue,
  (open) => {
    if (open) syncFromProps();
  }
);

watch(
  () => props.initialManual,
  () => {
    if (props.modelValue) syncFromProps();
  },
  { deep: true }
);

const formatMoney = (n: number) => formatWageNumber(Math.max(0, Math.round(n)));

const displayRows = computed(() =>
  props.rows.map((row) => ({
    ...row,
    hoursLabel: formatHomeWagePremiumHoursDisplay(row.hours)
      ? `${formatHomeWagePremiumHoursDisplay(row.hours)}h`
      : '-',
  }))
);

const manualTotal = computed(() =>
  localAmounts.overtime + localAmounts.night + localAmounts.holiday
);

const returnPreview = computed(() =>
  Math.max(0, props.autoAllocatedTotal - manualTotal.value)
);

const onMoneyInput = (key: PremiumAdjustKey, event: Event) => {
  const target = event.target as HTMLInputElement;
  const cleaned = target.value.replace(/[^\d]/g, '');
  localDisplay[key] = cleaned;
  localAmounts[key] = parseWageNumber(cleaned);
};

const onMoneyBlur = (key: PremiumAdjustKey) => {
  const val = Math.max(0, localAmounts[key] || 0);
  localAmounts[key] = val;
  localDisplay[key] = val > 0 ? formatWageNumber(val) : '';
};

const onApply = () => {
  emit('apply', {
    overtime: Math.max(0, localAmounts.overtime),
    night: Math.max(0, localAmounts.night),
    holiday: Math.max(0, localAmounts.holiday),
  });
};
</script>

<style lang="scss">
@import '@/assets/styles/components/home-wage-premium-adjust';
</style>
